import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { Download } from "lucide-react";

import { HoursByZone, LeaveMix, PunctualityTrend } from "@/components/dashboard/charts/report-charts";
import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { SortHeader } from "@/components/dashboard/data-table/sort-header";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { PageHeader } from "@/components/dashboard/page-header";
import { PrintTrigger } from "@/components/dashboard/print-trigger";
import {
  leaveGroups,
  nav,
  reports as copy,
} from "@/content";
import { can, scopeToSupervisorId } from "@/constants/permissions";
import { buildQuery, readNumber, readSort } from "@/utils/query-params";
import {
  REPORT_SORT_COLUMNS,
  getReportPayload,
  resolveRange,
  sortReport,
} from "@/api/reports";
import { listSupervisors, listZoneOptions } from "@/api/employees";
import { listPunchGuards } from "@/api/attendance";
import { addDateKey, instantAt, toDateKey } from "@/utils/time";
import { cn } from "@/utils";
import { useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";
import { PageError, PageLoading } from "@/components/dashboard/page-state";
import { apiUrl } from "@/config";

const BASE_PATH = "/dashboard/reports";

/**
 * Reports.
 *
 * The prototype's most impressive screen and its least trustworthy: both headline
 * metrics and both chart series were wrong, for reasons documented at length in
 * `services/reports.ts`. The numbers here are computed against the *published*
 * rota, in Riyadh time, from punches matched to their shift at ingestion.
 *
 * Admins see everyone; a supervisor sees their own team. The prototype's admin
 * branch listed every guard system-wide and then 404'd on most of them, because
 * its options came from a different query than its data. Here both come from the
 * same scoped call.
 */
export const Route = createFileRoute("/_main/dashboard/reports")({
  component: () => (
    <RequirePermission action="reports.read">
      <ReportsPage />
    </RequirePermission>
  ),
});

function ReportsPage() {
  const { user, settings } = useSession();
  useDocumentTitle(copy.title);
  const params = useQueryParams();
  const fmt = useFmt();
  const scope = scopeToSupervisorId(user);

  const rawFrom = typeof params.from === "string" ? params.from : undefined;
  const rawTo = typeof params.to === "string" ? params.to : undefined;
  const range = resolveRange({ from: rawFrom, to: rawTo }, settings.timeZone);
  /* Said out loud when it happens. The prototype accepted an inverted range and
     returned nothing, which looked exactly like a quiet period. */
  const swapped = Boolean(rawFrom && rawTo && rawFrom > rawTo);

  const sort = readSort(params, REPORT_SORT_COLUMNS, "guard");
  const filters = {
    range,
    guardId: readNumber(params, "guard"),
    zoneId: readNumber(params, "zone"),
    supervisorId: readNumber(params, "supervisor"),
    scopeToSupervisorId: scope,
    timeZone: settings.timeZone,
  };

  /* All four aggregates and their totals in one response. Four separate calls
     from a separately deployed front end would each re-derive the same window and
     the same scope, and would report the range four times. */
  const reportQuery = useQuery({
    queryKey: ["reports", "summary", filters],
    queryFn: () => getReportPayload(filters),
  });

  /* Options from the same scope as the data — the mismatch that made the
     prototype's guard selector 404 on most of its own entries. */
  const guardsQuery = useQuery({
    queryKey: ["attendance", "guards"],
    queryFn: listPunchGuards,
    staleTime: 5 * 60_000,
  });
  const zonesQuery = useQuery({
    queryKey: ["employees", "zoneOptions"],
    queryFn: listZoneOptions,
    staleTime: 5 * 60_000,
  });
  const supervisorsQuery = useQuery({
    queryKey: ["employees", "supervisors"],
    queryFn: listSupervisors,
    staleTime: 5 * 60_000,
  });

  const guards = guardsQuery.data ?? [];
  const zones = zonesQuery.data ?? [];
  const supervisors = supervisorsQuery.data ?? [];

  /* Returned early: a report with its totals missing but its table drawn is a
     document somebody would screenshot and act on. */
  if (reportQuery.isPending) return <PageLoading shape="tiles" />;
  if (reportQuery.isError && !reportQuery.data) {
    return (
      <PageError
        error={reportQuery.error}
        onRetry={() => {
          void reportQuery.refetch();
        }}
      />
    );
  }
  if (!reportQuery.data) return <PageLoading />;

  const report = reportQuery.data;
  const rows = report?.rows ?? [];
  const trend = report?.trend ?? [];
  const hoursByZone = report?.hoursByZone ?? [];
  const leaveMix = report?.leaveMix ?? [];
  const totals = report?.totals;
  const sorted = sortReport(rows, sort);
  const canExport = can(user.role, "reports.export");

  const today = toDateKey(new Date(), settings.timeZone);
  const presets = [
    { label: copy.presetToday, from: today, to: today },
    { label: copy.preset7, from: addDateKey(today, -6, settings.timeZone), to: today },
    { label: copy.preset30, from: addDateKey(today, -29, settings.timeZone), to: today },
    {
      label: copy.presetThisMonth,
      from: `${today.slice(0, 7)}-01`,
      to: today,
    },
    /* Last month, whole. The range a payroll run is reconciled against, and the
       one preset an operator reaches for on the first of the month. */
    ...(() => {
      const firstOfThis = `${today.slice(0, 7)}-01`;
      const lastOfPrevious = addDateKey(firstOfThis, -1, settings.timeZone);
      return [
        {
          label: copy.presetLastMonth,
          from: `${lastOfPrevious.slice(0, 7)}-01`,
          to: lastOfPrevious,
        },
      ];
    })(),
  ];

  const zoneColor = new Map(zones.map((zone) => [zone.name, zone.color]));

  const exportQuery = buildQuery(params, { from: range.from, to: range.to });

  return (
    /* `print:` utilities rather than a separate stylesheet: the page *is* the
       report, and a print rule that lives next to the element it hides cannot
       drift from it. The prototype had no print support at all. */
    <div className="mx-auto w-full max-w-shell px-6 py-8 print:max-w-none print:px-0 print:py-0">
      <PageHeader
        eyebrow={nav.groups.analytics}
        title={copy.title}
        lead={copy.lead}
        className="print:border-0 print:pb-2"
        actions={
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            {canExport ? (
              <a
                href={apiUrl(`/api/export/report${exportQuery}`)}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
              >
                <Download aria-hidden className="size-4" strokeWidth={1.6} />
                {copy.exportCsv}
              </a>
            ) : null}
            <PrintTrigger label={copy.print} />
          </div>
        }
      />

      {/* Range and filters. A real form so it works without JavaScript, and the
          presets are links so each is bookmarkable. */}
      <form
        action={BASE_PATH}
        method="get"
        className="mt-6 flex flex-wrap items-end gap-3 print:hidden"
      >
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold text-n6">{copy.rangeFrom}</span>
          <input
            type="date"
            name="from"
            defaultValue={range.from}
            className="tabular h-10 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold text-n6">{copy.rangeTo}</span>
          <input
            type="date"
            name="to"
            defaultValue={range.to}
            className="tabular h-10 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>

        <Select
          name="guard"
          label={copy.filterGuard}
          value={typeof params.guard === "string" ? params.guard : ""}
          options={guards.map((guard) => ({ value: String(guard.id), label: guard.name }))}
        />
        <Select
          name="zone"
          label={copy.filterZone}
          value={typeof params.zone === "string" ? params.zone : ""}
          options={zones.map((zone) => ({ value: String(zone.id), label: zone.name }))}
        />
        {scope === null ? (
          <Select
            name="supervisor"
            label={copy.filterSupervisor}
            value={typeof params.supervisor === "string" ? params.supervisor : ""}
            options={supervisors.map((supervisor) => ({
              value: String(supervisor.id),
              label: supervisor.name,
            }))}
          />
        ) : null}

        <button
          type="submit"
          className="h-10 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
        >
          {copy.apply}
        </button>
      </form>

      <div className="mt-3 flex flex-wrap items-center gap-2 print:hidden">
        {presets.map((preset) => {
          const active = preset.from === range.from && preset.to === range.to;
          return (
            <Link
              key={preset.label}
              to={`${BASE_PATH}${buildQuery(params, { from: preset.from, to: preset.to })}`}
              className={cn(
                "rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors duration-200 ease-brand",
                active
                  ? "border-orange/45 bg-orange/8 text-orange-700"
                  : "border-n4 bg-n0 text-n7 hover:bg-n2",
              )}
            >
              {preset.label}
            </Link>
          );
        })}
      </div>

      <p className="tabular mt-3 text-[11px] font-semibold text-n7">
        {copy.rangeApplied} {fmt.dateNumeric(instantAt(range.from, "12:00", settings.timeZone))} –{" "}
        {fmt.dateNumeric(instantAt(range.to, "12:00", settings.timeZone))}
        {swapped ? <span className="ms-2 text-orange-700">{copy.rangeSwapped}</span> : null}
      </p>

      {/* The KPI row: a `gap-px` hairline grid on `bg-n4`, not a row of cards.
          Every figure is derived from the table below, so the two cannot
          disagree. */}
      <dl className="mt-6 grid gap-px overflow-hidden rounded-lg bg-n4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi
          label={copy.kpiPunctuality}
          value={totals.punctualityRate === null ? copy.noData : fmt.percent(totals.punctualityRate)}
          muted={totals.punctualityRate === null}
        />
        <Kpi
          label={copy.kpiCoverage}
          value={totals.coverageRate === null ? copy.noData : fmt.percent(totals.coverageRate)}
          muted={totals.coverageRate === null}
        />
        <Kpi label={copy.kpiWorked} value={fmt.duration(totals.workedMinutes)} />
        <Kpi label={copy.kpiScheduled} value={fmt.duration(totals.scheduledMinutes)} />
        <Kpi
          label={copy.kpiLate}
          value={fmt.duration(totals.lateMinutes)}
          attention={totals.lateMinutes > 0}
        />
        <Kpi
          label={copy.kpiAbsences}
          value={fmt.count(totals.absences)}
          attention={totals.absences > 0}
        />
        <Kpi
          label={copy.kpiOutside}
          value={fmt.count(totals.outsideZoneCount)}
          attention={totals.outsideZoneCount > 0}
        />
        <Kpi
          label={copy.kpiUnclosed}
          value={fmt.count(totals.unclosedPunches)}
          attention={totals.unclosedPunches > 0}
          href={totals.unclosedPunches > 0 ? "/dashboard/attendance" : undefined}
        />
      </dl>

      <section className="mt-8">
        <h2 className="text-sm font-semibold text-n8">{copy.trendTitle}</h2>
        <p className="mt-1 max-w-[70ch] text-xs text-n6">{copy.trendLead}</p>
        <div className="ticks [--tick-inset:12px] mt-4 rounded-lg border border-n3 bg-n0 p-4 shadow-1">
          <PunctualityTrend points={trend} numerals={user.numeralSystem} />
        </div>
      </section>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <section className="min-w-0">
          <h2 className="text-sm font-semibold text-n8">{copy.hoursByZoneTitle}</h2>
          <div className="mt-4 rounded-lg border border-n3 bg-n0 p-4 shadow-1">
            <HoursByZone
              data={hoursByZone.map((row) => ({
                zoneName: row.zoneName,
                minutes: row.minutes,
                color: zoneColor.get(row.zoneName),
              }))}
              numerals={user.numeralSystem}
            />
          </div>
        </section>

        <section className="min-w-0">
          <h2 className="text-sm font-semibold text-n8">{copy.leaveMixTitle}</h2>
          <div className="mt-4 rounded-lg border border-n3 bg-n0 p-4 shadow-1">
            <LeaveMix
              data={leaveMix.map((row) => ({
                label: `${row.nameAr} · ${leaveGroups[row.group as keyof typeof leaveGroups]}`,
                days: row.days,
                color: row.color ?? "var(--orange)",
              }))}
              numerals={user.numeralSystem}
            />
          </div>
        </section>
      </div>

      <TableShell className="mt-8 print:border-0 print:shadow-none">
        <Table>
          <THead>
            <SortHeader
              column="guard"
              label={copy.columnGuard}
              params={params}
              basePath={BASE_PATH}
              current={sort}
            />
            <TH>{copy.columnSupervisor}</TH>
            <TH numeric>{copy.columnScheduled}</TH>
            <SortHeader
              column="workedMinutes"
              label={copy.columnWorked}
              params={params}
              basePath={BASE_PATH}
              current={sort}
              numeric
            />
            <SortHeader
              column="lateMinutes"
              label={copy.columnLate}
              params={params}
              basePath={BASE_PATH}
              current={sort}
              numeric
            />
            <TH numeric>{copy.columnEarly}</TH>
            <SortHeader
              column="punctuality"
              label={copy.columnPunctuality}
              params={params}
              basePath={BASE_PATH}
              current={sort}
              numeric
            />
            <SortHeader
              column="absences"
              label={copy.columnAbsences}
              params={params}
              basePath={BASE_PATH}
              current={sort}
              numeric
            />
            <TH numeric>{copy.columnLeave}</TH>
            <TH numeric>{copy.columnOutside}</TH>
          </THead>

          <TBody>
            {sorted.length === 0 ? (
              <TEmpty colSpan={10}>
                <EmptyState
                  variant="empty"
                  title={copy.emptyTitle}
                  lead={copy.emptyLead}
                  actionHref="/dashboard/shifts"
                  actionLabel={nav.shifts}
                />
              </TEmpty>
            ) : (
              sorted.map((row) => {
                const rate =
                  row.scheduledPunches === 0
                    ? null
                    : row.onTimePunches / row.scheduledPunches;

                return (
                  <TR key={row.guardId}>
                    <TD>
                      <div className="flex items-center gap-2.5">
                        <PersonAvatar name={row.guardName} photoUrl={row.photoUrl} />
                        <Link
                          to={`/dashboard/employees/${row.guardId}`}
                          className="font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                        >
                          {row.guardName}
                        </Link>
                      </div>
                    </TD>
                    <TD>
                      <span className="text-xs text-n6">{row.supervisorName ?? "—"}</span>
                    </TD>
                    <TD numeric>
                      <span className="text-xs">{fmt.duration(row.scheduledMinutes)}</span>
                    </TD>
                    <TD numeric>
                      <span className="text-xs">{fmt.duration(row.workedMinutes)}</span>
                    </TD>
                    <TD numeric>
                      <span
                        className={cn("text-xs", row.lateMinutes > 0 && "font-bold text-orange-700")}
                      >
                        {row.lateMinutes === 0 ? "—" : fmt.duration(row.lateMinutes)}
                      </span>
                    </TD>
                    <TD numeric>
                      <span className="text-xs">
                        {row.earlyLeaveMinutes === 0 ? "—" : fmt.duration(row.earlyLeaveMinutes)}
                      </span>
                    </TD>
                    <TD numeric>
                      {/* "Not scheduled" rather than 0% — the distinction the
                          prototype's all-zeros guard was rendered without, as an
                          invisible bar with no annotation. */}
                      {rate === null ? (
                        <span className="text-[11px] text-n6">{copy.notScheduled}</span>
                      ) : (
                        <span className="text-xs font-semibold">{fmt.percent(rate)}</span>
                      )}
                    </TD>
                    <TD numeric>
                      <span
                        className={cn("text-xs", row.absences > 0 && "font-bold text-destructive")}
                      >
                        {row.absences === 0 ? "—" : fmt.count(row.absences)}
                      </span>
                    </TD>
                    <TD numeric>
                      <span className="text-xs">
                        {row.leaveDays === 0 ? "—" : fmt.count(row.leaveDays)}
                      </span>
                    </TD>
                    <TD numeric>
                      <span
                        className={cn(
                          "text-xs",
                          row.outsideZoneCount > 0 && "font-bold text-orange-700",
                        )}
                      >
                        {row.outsideZoneCount === 0 ? "—" : fmt.count(row.outsideZoneCount)}
                      </span>
                    </TD>
                  </TR>
                );
              })
            )}
          </TBody>
        </Table>
      </TableShell>

      {canExport ? (
        <p className="mt-3 text-[11px] text-n6 print:hidden">{copy.exportNote}</p>
      ) : null}

      {/* Only on paper: a printout with no date on it is unattributable. */}
      <p className="tabular mt-4 hidden text-[11px] text-n6 print:block">
        {copy.printedAt}: {fmt.dateTime(new Date())}
      </p>
    </div>
  );
}

function Kpi({
  label,
  value,
  attention,
  muted,
  href,
}: {
  label: string;
  value: string;
  attention?: boolean;
  muted?: boolean;
  href?: string;
}) {
  const body = (
    <>
      <dt className="text-[11px] font-semibold tracking-[0.04em] text-n6">{label}</dt>
      <dd
        className={cn(
          "tabular mt-2 text-lg font-bold",
          muted ? "text-n6" : attention ? "text-orange-700" : "text-n9",
        )}
      >
        {value}
      </dd>
    </>
  );

  /* A tile that links somewhere real, when there is somewhere to go. The
     prototype's stat row was inert. */
  return href ? (
    <Link
      to={href}
      className="bg-n0 p-5 transition-colors duration-200 ease-brand hover:bg-n2"
    >
      {body}
    </Link>
  ) : (
    <div className="bg-n0 p-5">{body}</div>
  );
}

function Select({
  name,
  label,
  value,
  options,
}: {
  name: string;
  label: string;
  value: string;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold text-n6">{label}</span>
      <select
        name={name}
        defaultValue={value}
        className="select-field h-10 min-w-36 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
      >
        <option value="">{label}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
